/**
 * Shared quality-score color helper (axis-3).
 *
 * Maps a score onto the `--score-{1..5}` token ramp (red -> green, defined in
 * index.css). Replaces the duplicated inline color ladders that previously
 * lived in identity-manager / identity-detail.
 */

export type ScoreBand = 1 | 2 | 3 | 4 | 5

/** Bucket a score into a 1..5 band. `max` is the score's upper bound (default 5). */
export function scoreBand(score: number, max = 5): ScoreBand {
  const band = Math.ceil((Math.max(0, score) / max) * 5)
  return Math.min(5, Math.max(1, band)) as ScoreBand
}

// Static class strings per band — written out in full so Tailwind's scanner
// generates these utilities (it cannot see runtime-built `bg-score-${n}`).
const BAND_CLASSES: Record<ScoreBand, { bg: string; text: string; ring: string }> = {
  1: { bg: 'bg-score-1', text: 'text-score-1', ring: 'stroke-score-1' },
  2: { bg: 'bg-score-2', text: 'text-score-2', ring: 'stroke-score-2' },
  3: { bg: 'bg-score-3', text: 'text-score-3', ring: 'stroke-score-3' },
  4: { bg: 'bg-score-4', text: 'text-score-4', ring: 'stroke-score-4' },
  5: { bg: 'bg-score-5', text: 'text-score-5', ring: 'stroke-score-5' },
}

/** Background class for a score, e.g. `bg-score-3`. */
export function scoreColor(score: number, max = 5): string {
  return BAND_CLASSES[scoreBand(score, max)].bg
}

/** bg / text / ring(stroke) classes for a score, all on the same band token. */
export function scoreColorSet(score: number, max = 5): { bg: string; text: string; ring: string } {
  return BAND_CLASSES[scoreBand(score, max)]
}
